import React from "react";

const emptyState = {
  results: [],
  loading: false,
  fetched: false,
  hasMore: false,
  error: null,
};

// Each query owns its requests so late responses cannot replace a newer search.
export default function useSearchResults({
  query,
  enabled,
  loadPage,
  pageSize,
}) {
  const [state, setState] = React.useState(emptyState);
  const loadMoreRef = React.useRef(() => {});

  React.useEffect(() => {
    setState(emptyState);
    if (!enabled || !query.trim()) {
      loadMoreRef.current = () => {};
      return;
    }
    let active = true;
    let pending = false;
    let page = 0;
    let hasMore = true;
    const controller = new AbortController();
    const load = async () => {
      if (!active || pending || !hasMore) return;
      pending = true;
      setState((previous) => ({ ...previous, loading: true, error: null }));
      try {
        const results = await loadPage(query, page + 1, controller.signal);
        if (!active) return;
        page += 1;
        hasMore = Boolean(pageSize && results.length >= pageSize);
        setState((previous) => ({
          results: page === 1 ? results : [...previous.results, ...results],
          loading: false,
          fetched: true,
          hasMore,
          error: null,
        }));
      } catch (error) {
        if (!active || error.name === "AbortError") return;
        setState((previous) => ({
          ...previous,
          loading: false,
          fetched: true,
          error,
        }));
      } finally {
        pending = false;
      }
    };
    loadMoreRef.current = load;
    load();
    return () => {
      active = false;
      controller.abort();
      loadMoreRef.current = () => {};
    };
  }, [query, enabled, loadPage, pageSize]);

  return { ...state, loadMore: () => loadMoreRef.current() };
}
